import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { isBrowser } from '../utils/browserGuards';
import { featureManager, autoDisableOnError } from '../utils/featureFlags';

// Get backend URL with proper fallback for production
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

/**
 * Session monitoring hook that tracks session expiry and handles timeout warnings
 * @param {Object} options - Configuration options
 * @param {Function} options.onTimeout - Callback when session expires
 * @param {Function} options.emergencySave - Function to save data before timeout
 * @param {boolean} options.isDirty - Whether there are unsaved changes
 */
export const useSessionMonitor = ({ onTimeout, emergencySave, isDirty = false }) => {
  const [timeToExpiry, setTimeToExpiry] = useState(null);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);
  const [isEnabled, setIsEnabled] = useState(featureManager.isEnabled('FEATURE_SESSION_MONITOR'));
  
  const intervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const emergencySaveTriggered = useRef(false);
  
  // Disable session monitoring if environment doesn't support it
  useEffect(() => {
    if (!isBrowser || !API) {
      setIsEnabled(false);
      autoDisableOnError('FEATURE_SESSION_MONITOR', 'Browser or API not available');
    }
  }, []);
  
  // Get session TTL from server with error handling
  const getSessionTTL = useCallback(async () => {
    if (!isEnabled || !API) {
      return null;
    }
    
    try {
      // DIAGNOSTIC: Log session TTL check
      console.debug('[useSessionMonitor] Checking session TTL...');
      const response = await axios.get(`${API}/session/ttl`, {
        timeout: 5000 // 5 second timeout
      });
      console.debug('[useSessionMonitor] TTL response:', response.data.seconds, 'seconds');
      return response.data.seconds || 0;
    } catch (error) {
      console.warn('[useSessionMonitor] Failed to get session TTL:', error);
      
      // Check if this is a 404 (endpoint not available) - only then disable
      if (error.response?.status === 404) {
        console.warn('[useSessionMonitor] Session TTL endpoint not available, disabling session monitor');
        setIsEnabled(false);
        autoDisableOnError('FEATURE_SESSION_MONITOR', 'ttl_endpoint_unavailable');
        return null;
      }
      
      // For 502/503/504 (server errors) - use fallback, DON'T disable
      // These are temporary issues and session should continue normally
      if (error.response?.status === 502 || error.response?.status === 503 || error.response?.status === 504) {
        console.warn('[useSessionMonitor] Server temporarily unavailable (502/503/504), using fallback TTL');
        return 30 * 60; // 30 minutes fallback
      }
      
      // For network errors or timeouts - use fallback, DON'T disable
      if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || error.code === 'ERR_BAD_RESPONSE') {
        console.warn('[useSessionMonitor] Network error, using fallback TTL');
        return 30 * 60; // 30 minutes fallback
      }
      
      // For other errors, return fallback (assume 30 minutes)
      console.warn('[useSessionMonitor] Using fallback TTL of 30 minutes');
      return 30 * 60;
    }
  }, [isEnabled]);
  
  // Refresh session token with error handling
  const refreshSession = useCallback(async () => {
    if (!isEnabled || !API) {
      return false;
    }
    
    try {
      setIsRefreshingToken(true);
      const response = await axios.post(`${API}/session/refresh`, {}, {
        timeout: 10000 // 10 second timeout
      });
      
      if (response.data.success) {
        console.log('Session refreshed successfully');
        setShowTimeoutModal(false);
        setCountdown(0);
        emergencySaveTriggered.current = false;
        return true;
      }
      return false;
    } catch (error) {
      console.error('Session refresh failed:', error);
      
      // Check if this is a 404 (endpoint not available)
      if (error.response?.status === 404) {
        console.warn('Session refresh endpoint not available, disabling session monitor');
        setIsEnabled(false);
        autoDisableOnError('FEATURE_SESSION_MONITOR', 'refresh_endpoint_unavailable');
      }
      
      return false;
    } finally {
      setIsRefreshingToken(false);
    }
  }, [isEnabled]);
  
  // Handle timeout modal countdown
  const startCountdown = useCallback((seconds) => {
    setCountdown(seconds);
    
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        const newCount = prev - 1;
        
        // Emergency save at T-60s
        if (newCount === 60 && isDirty && !emergencySaveTriggered.current) {
          console.log('Emergency save triggered at T-60s');
          emergencySaveTriggered.current = true;
          if (emergencySave) {
            emergencySave();
          }
        }
        
        // Auto-logout when countdown reaches 0
        if (newCount <= 0) {
          clearInterval(countdownIntervalRef.current);
          setShowTimeoutModal(false);
          if (onTimeout) {
            onTimeout();
          }
          return 0;
        }
        
        return newCount;
      });
    }, 1000);
  }, [isDirty, emergencySave, onTimeout]);
  
  // Monitor session expiry with safety checks
  // NOTE: Polling is DISABLED by default to prevent duplicate calls with IdleManager
  // IdleManager already polls /api/session/ping every 60s for session validation
  // This hook now only activates polling when showTimeoutModal is already true (countdown mode)
  useEffect(() => {
    // Skip polling if not enabled or if IdleManager is handling session checks
    // Only run polling when countdown modal is active
    if (!isEnabled || !isBrowser || !showTimeoutModal) {
      return;
    }
    
    const checkSession = async () => {
      const ttl = await getSessionTTL();
      
      // If TTL is null, session monitoring is disabled
      if (ttl === null) {
        return;
      }
      
      setTimeToExpiry(ttl);
      
      // Show modal at T-90s
      if (ttl <= 90 && ttl > 0 && !showTimeoutModal) {
        console.log('[useSessionMonitor] Session timeout warning triggered:', ttl, 'seconds remaining');
        setShowTimeoutModal(true);
        startCountdown(ttl);
      }
      
      // Auto-logout if session already expired
      if (ttl <= 0) {
        if (onTimeout) {
          onTimeout();
        }
      }
    };
    
    // Only poll when modal is showing (countdown mode) - every 10s for accurate countdown
    intervalRef.current = setInterval(checkSession, 10000);
    
    // Initial check
    checkSession();
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [getSessionTTL, showTimeoutModal, startCountdown, onTimeout, isEnabled]);
  
  // Stay signed in (refresh token)
  const staySignedIn = useCallback(async () => {
    const success = await refreshSession();
    if (!success) {
      // If refresh fails, still trigger emergency save and logout
      if (isDirty && emergencySave && !emergencySaveTriggered.current) {
        emergencySaveTriggered.current = true;
        await emergencySave();
      }
      if (onTimeout) {
        onTimeout();
      }
    }
  }, [refreshSession, isDirty, emergencySave, onTimeout]);
  
  // Sign out now
  const signOutNow = useCallback(async () => {
    // Trigger emergency save before signing out
    if (isDirty && emergencySave && !emergencySaveTriggered.current) {
      emergencySaveTriggered.current = true;
      await emergencySave();
    }
    
    setShowTimeoutModal(false);
    if (onTimeout) {
      onTimeout();
    }
  }, [isDirty, emergencySave, onTimeout]);
  
  // Close modal (but keep countdown running)
  const closeModal = useCallback(() => {
    setShowTimeoutModal(false);
  }, []);
  
  // Format countdown time
  const formatCountdown = useCallback((seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);
  
  return {
    timeToExpiry,
    showTimeoutModal,
    countdown,
    countdownFormatted: formatCountdown(countdown),
    isRefreshingToken,
    staySignedIn,
    signOutNow,
    closeModal
  };
};